import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdBanner from '../components/AdBanner';
import PremiumGuideCta from '../components/PremiumGuideCta';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/blog/${slug}`);
        if (response.status === 404) {
          setError('Post not found');
          return;
        }
        if (!response.ok) throw new Error('Failed to fetch post');
        
        const data = await response.json();
        setPost(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecentPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/blog/recent?limit=5`);
        if (response.ok) {
          const data = await response.json();
          setRecentPosts(data.filter(p => p.slug !== slug));
        }
      } catch (err) {
        console.error('Error fetching recent posts:', err);
      }
    };

    fetchPost();
    fetchRecentPosts();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (value) => {
    const categories = {
      'invoicing': 'Invoicing Basics & Best Practices',
      'bill-of-sale': 'Bill of Sale Articles',
      'online-safety': 'Online Safety & Scam Prevention',
      'buying-selling': 'Buying and Selling Tips',
      'business': 'General Business Advice',
      'thrifting': 'Thrifting to Marketplace Pipeline'
    };
    return categories[value] || value;
  };

  // Social sharing URLs
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = post?.title || '';

  const socialLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}`,
    reddit: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`
  };

  if (loading) {
    return (
      <div className="blog-page">
        <div className="blog-loading container">
          <div className="loading-spinner"></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-page">
        <div className="blog-error container">
          <h1>Post Not Found</h1>
          <p>{error || 'This post does not exist or has been removed.'}</p>
          <Link to="/blog" className="btn-primary">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page blog-post-page">
      <Helmet>
        <title>{post.seoTitle || post.title} | PrivateSold Blog</title>
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://privatesold.com/blog/${post.slug}`} />
        {post.featuredImage?.url && (
          <meta property="og:image" content={post.featuredImage.url} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt} />
        {post.canonicalUrl ? (
          <link rel="canonical" href={post.canonicalUrl} />
        ) : (
          <link rel="canonical" href={`https://privatesold.com/blog/${post.slug}`} />
        )}
        <meta property="article:published_time" content={post.publishDate} />
        {post.tags?.length > 0 && (
          <meta name="keywords" content={post.tags.join(', ')} />
        )}
      </Helmet>

      <article className="blog-post-container container">
        <div className="blog-post-main">
          {/* Breadcrumb */}
          <nav className="blog-breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/blog">Blog</Link>
            <span>›</span>
            <Link to={`/blog?category=${post.category}`}>{getCategoryLabel(post.category)}</Link>
          </nav>

          {/* Featured Image */}
          {post.featuredImage?.url && (
            <div className="blog-post-featured-image">
              <img src={post.featuredImage.url} alt={post.featuredImage.alt || post.title} />
            </div>
          )}

          {/* Post Header */}
          <header className="blog-post-header">
            <div className="blog-post-meta">
              <span className="blog-post-category">{getCategoryLabel(post.category)}</span>
              <span className="blog-post-date">{formatDate(post.publishDate)}</span>
              {post.views > 0 && (
                <span className="blog-post-views">{post.views.toLocaleString()} views</span>
              )}
            </div>
            <h1>{post.title}</h1>
            {post.author && (
              <div className="blog-post-author">
                {post.author.avatar && (
                  <img src={post.author.avatar} alt={post.author.name} className="author-avatar" />
                )}
                <span>By {post.author.name}</span>
              </div>
            )}
          </header>

          {/* Ad Banner */}
          <AdBanner position="blog-post-top" />

          {/* Post Content */}
          <div 
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="blog-post-tags">
              <span>Tags:</span>
              {post.tags.map(tag => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          )}

          {/* Social Sharing */}
          <div className="blog-post-share">
            <span>Share this article:</span>
            <div className="share-buttons">
              <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="share-btn twitter">
                Twitter
              </a>
              <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="share-btn linkedin">
                LinkedIn
              </a>
              <a href={socialLinks.pinterest} target="_blank" rel="noopener noreferrer" className="share-btn pinterest">
                Pinterest
              </a>
              <a href={socialLinks.reddit} target="_blank" rel="noopener noreferrer" className="share-btn reddit">
                Reddit
              </a>
            </div>
          </div>

          {/* Premium Guide CTA - Show if this post has a premium PDF */}
          {post.hasPremiumPdf && (
            <PremiumGuideCta post={post} />
          )}

          {/* CTA */}
          <div className="blog-post-cta">
            <h3>Ready to Create Your Bill of Sale?</h3>
            <p>Generate professional documents in minutes. Free to start!</p>
            <Link to="/bill-of-sale" className="btn-primary">Create Bill of Sale</Link>
          </div>

          {/* Ad Banner */}
          <AdBanner position="blog-post-bottom" />

          {/* Related Posts */}
          {post.relatedPosts?.length > 0 && (
            <div className="blog-related-posts">
              <h3>Related Articles</h3>
              <div className="related-posts-grid">
                {post.relatedPosts.map(related => (
                  <Link key={related._id} to={`/blog/${related.slug}`} className="related-post-card">
                    {related.featuredImage?.url && (
                      <img src={related.featuredImage.url} alt={related.title} />
                    )}
                    <h4>{related.title}</h4>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          <div className="sidebar-widget">
            <h3>Recent Posts</h3>
            <ul className="recent-posts-list">
              {recentPosts.slice(0, 5).map(recent => (
                <li key={recent._id}>
                  <Link to={`/blog/${recent.slug}`}>
                    <span className="recent-post-title">{recent.title}</span>
                    <span className="recent-post-date">{formatDate(recent.publishDate)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-widget sidebar-cta">
            <h3>Free Bill of Sale Generator</h3>
            <p>Create professional documents for your private sales.</p>
            <Link to="/bill-of-sale" className="sidebar-cta-btn">Get Started</Link>
          </div>

          <AdBanner position="blog-sidebar" />
        </aside>
      </article>
    </div>
  );
}

export default BlogPost;
